import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Package, Users, Download, Share2, MessageSquare, Send, X, Calendar } from "lucide-react";
import { buildReportSummary, downloadReport, shareReport } from "@/lib/reportPdf";
import { toast } from "sonner";

const RANGES = [
  { key: "today", label: "TODAY" },
  { key: "week", label: "7 DAYS" },
  { key: "month", label: "30 DAYS" },
  { key: "year", label: "1 YEAR" },
  { key: "all", label: "ALL TIME" },
  { key: "custom", label: "CUSTOM" },
];

export default function Reports() {
  const [tab, setTab] = useState("sku");
  const [range, setRange] = useState("month");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sku, setSku] = useState([]);
  const [agent, setAgent] = useState([]);
  const [adminPhone, setAdminPhone] = useState("");
  const [shareOpen, setShareOpen] = useState(false);
  const [customPhone, setCustomPhone] = useState("");

  const params = useMemo(() => {
    if (range === "custom") {
      const p = {};
      if (dateFrom) p.date_from = dateFrom;
      if (dateTo) p.date_to = dateTo;
      return p;
    }
    if (range === "all") return {};
    return { range };
  }, [range, dateFrom, dateTo]);

  const rangeLabel = useMemo(() => {
    if (range === "custom") return `${dateFrom || "start"} → ${dateTo || "today"}`;
    return RANGES.find((r) => r.key === range)?.label || "";
  }, [range, dateFrom, dateTo]);

  const reload = () => {
    api.get("/reports/sku", { params }).then((r) => setSku(r.data || [])).catch(() => {});
    api.get("/reports/agent", { params }).then((r) => setAgent(r.data || [])).catch(() => {});
  };

  useEffect(() => { reload(); }, [params]); // eslint-disable-line
  useEffect(() => { api.get("/config/admin-phone").then((r) => setAdminPhone(r.data.admin_phone)).catch(() => {}); }, []);

  const currentRows = tab === "sku" ? sku : agent;
  const fileName = `${tab}-report-${range === "custom" ? `${dateFrom || "from"}_${dateTo || "to"}` : range}.pdf`;

  const exportCsv = () => {
    const rows = currentRows;
    if (!rows.length) return;
    const headers = Object.keys(rows[0]).filter((k) => k !== "customers");
    const csv = [headers.join(",")].concat(
      rows.map((r) => headers.map((h) => `"${String(r[h] ?? "").replace(/"/g, '""')}"`).join(","))
    ).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = fileName.replace(".pdf", ".csv"); a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadPdf = async () => {
    await downloadReport(tab, currentRows, rangeLabel, fileName);
    toast.success("PDF downloaded");
  };

  const handleSharePdf = async () => {
    const ok = await shareReport(tab, currentRows, rangeLabel, fileName);
    if (!ok) {
      await downloadReport(tab, currentRows, rangeLabel, fileName);
      toast.message("PDF downloaded — attach in WhatsApp manually");
    }
  };

  const waLink = (phone) => {
    const clean = String(phone || "").replace(/[^\d]/g, "");
    const number = clean.length === 10 ? `91${clean}` : clean;
    const text = buildReportSummary(tab, currentRows, rangeLabel);
    return `https://wa.me/${number}?text=${encodeURIComponent(text)}`;
  };

  return (
    <div data-testid="reports-page">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.2em] text-[#E63946] font-bold mb-2">── ANALYTICS</div>
        <h1 className="font-heading font-black text-4xl tracking-tighter">REPORTS.</h1>
      </div>

      {/* Range bar */}
      <div className="brut-card p-4 mb-5" data-testid="range-bar">
        <div className="flex items-center gap-2 flex-wrap mb-3">
          <Calendar size={14} strokeWidth={3} />
          <div className="label-mono !mb-0 mr-2">Range:</div>
          {RANGES.map((r) => (
            <button
              key={r.key}
              onClick={() => setRange(r.key)}
              data-testid={`range-${r.key}`}
              className={`px-3 py-1.5 border-2 border-black text-xs uppercase tracking-wider font-bold
                ${range === r.key ? "bg-[#0A0A0A] text-white" : "bg-white hover:bg-[#F4F4F0]"}`}
            >
              {r.label}
            </button>
          ))}
        </div>
        {range === "custom" && (
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="label-mono">From</label>
              <input type="date" className="brut-input" data-testid="date-from"
                     value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            </div>
            <div>
              <label className="label-mono">To</label>
              <input type="date" className="brut-input" data-testid="date-to"
                     value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            </div>
          </div>
        )}
        <div className="text-xs uppercase tracking-wider text-[#666] mt-3">
          Showing: <b className="text-[#0A0A0A]">{rangeLabel}</b>
        </div>
      </div>

      {/* Tabs + actions */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <button onClick={() => setTab("sku")} data-testid="tab-sku"
                className={`brut-btn ${tab === "sku" ? "brut-btn-dark" : "brut-btn-secondary"}`}>
          <Package size={16} strokeWidth={3} /> SKU-WISE
        </button>
        <button onClick={() => setTab("agent")} data-testid="tab-agent"
                className={`brut-btn ${tab === "agent" ? "brut-btn-dark" : "brut-btn-secondary"}`}>
          <Users size={16} strokeWidth={3} /> AGENT-WISE
        </button>
        <div className="ml-auto flex flex-wrap gap-2">
          <button onClick={exportCsv} className="brut-btn brut-btn-secondary !py-2 text-xs" data-testid="export-csv">
            <Download size={14} strokeWidth={3} /> CSV
          </button>
          <button onClick={handleDownloadPdf} className="brut-btn brut-btn-secondary !py-2 text-xs" data-testid="download-pdf">
            <Download size={14} strokeWidth={3} /> PDF
          </button>
          <button onClick={handleSharePdf} className="brut-btn brut-btn-primary !py-2 text-xs" data-testid="share-pdf">
            <Share2 size={14} strokeWidth={3} /> SHARE PDF
          </button>
          <button onClick={() => setShareOpen(true)} className="brut-btn brut-btn-dark !py-2 text-xs" data-testid="wa-share-btn">
            <MessageSquare size={14} strokeWidth={3} /> SEND VIA WHATSAPP
          </button>
        </div>
      </div>

      {tab === "sku" ? (
        <div className="brut-card overflow-hidden" data-testid="sku-table">
          <table className="w-full text-sm">
            <thead className="bg-[#0A0A0A] text-white"><tr className="text-left">
              <th className="px-4 py-3 text-xs uppercase">Design #</th>
              <th className="px-4 py-3 text-xs uppercase">PCS</th>
              <th className="px-4 py-3 text-xs uppercase">SETS</th>
              <th className="px-4 py-3 text-xs uppercase">Total Qty</th>
              <th className="px-4 py-3 text-xs uppercase">Total Value</th>
              <th className="px-4 py-3 text-xs uppercase">Orders</th>
            </tr></thead>
            <tbody>
              {sku.length === 0 && <tr><td colSpan="6" className="px-4 py-10 text-center text-sm text-[#666] uppercase">No data for this range</td></tr>}
              {sku.map((r) => (
                <tr key={r.design_number} className="border-b border-black/15" data-testid={`sku-row-${r.design_number}`}>
                  <td className="px-4 py-3 font-bold">{r.design_number}</td>
                  <td className="px-4 py-3 font-mono">{r.total_pcs || 0}</td>
                  <td className="px-4 py-3 font-mono">{r.total_sets || 0}</td>
                  <td className="px-4 py-3 font-mono font-bold">{r.total_quantity}</td>
                  <td className="px-4 py-3 font-mono">₹{Math.round(r.total_amount).toLocaleString("en-IN")}</td>
                  <td className="px-4 py-3 font-mono">{r.order_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="brut-card overflow-hidden" data-testid="agent-table">
          <table className="w-full text-sm">
            <thead className="bg-[#0A0A0A] text-white"><tr className="text-left">
              <th className="px-4 py-3 text-xs uppercase">Agent</th>
              <th className="px-4 py-3 text-xs uppercase">Orders</th>
              <th className="px-4 py-3 text-xs uppercase">Customers</th>
              <th className="px-4 py-3 text-xs uppercase">Total Qty</th>
              <th className="px-4 py-3 text-xs uppercase">Total Value</th>
            </tr></thead>
            <tbody>
              {agent.length === 0 && <tr><td colSpan="5" className="px-4 py-10 text-center text-sm text-[#666] uppercase">No data for this range</td></tr>}
              {agent.map((r) => (
                <tr key={r.agent} className="border-b border-black/15" data-testid={`agent-row-${r.agent}`}>
                  <td className="px-4 py-3 font-bold">{r.agent}</td>
                  <td className="px-4 py-3 font-mono">{r.total_orders}</td>
                  <td className="px-4 py-3 font-mono">{r.unique_customers}</td>
                  <td className="px-4 py-3 font-mono">{r.total_quantity}</td>
                  <td className="px-4 py-3 font-mono">₹{Math.round(r.total_amount).toLocaleString("en-IN")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* WhatsApp share modal */}
      {shareOpen && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" data-testid="wa-modal">
          <div className="brut-card w-full max-w-md">
            <div className="flex items-center justify-between border-b-2 border-black px-4 py-3">
              <div className="font-heading font-black text-lg">SEND VIA WHATSAPP</div>
              <button onClick={() => setShareOpen(false)} className="brut-btn brut-btn-secondary !py-1 !px-2" data-testid="wa-modal-close">
                <X size={14} strokeWidth={3} />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <div className="text-xs text-[#666] uppercase tracking-wider">
                Pre-fills WhatsApp with a text summary. On mobile, also use SHARE PDF (above) to attach the PDF directly.
              </div>
              <a href={waLink(adminPhone)} target="_blank" rel="noreferrer"
                 onClick={() => setShareOpen(false)}
                 className="brut-btn brut-btn-dark w-full" data-testid="wa-admin-btn">
                <Send size={16} strokeWidth={3} /> SEND TO ADMIN ({adminPhone || "—"})
              </a>
              <div className="text-center text-xs text-[#666] uppercase tracking-wider">OR</div>
              <label className="label-mono">Any number (10-digit or with country code)</label>
              <input className="brut-input" data-testid="custom-phone-input"
                     placeholder="9876543210"
                     value={customPhone} onChange={(e) => setCustomPhone(e.target.value)} />
              <a
                href={customPhone ? waLink(customPhone) : "#"}
                onClick={(e) => {
                  if (!customPhone) { e.preventDefault(); toast.error("Enter a phone number"); return; }
                  setShareOpen(false);
                }}
                target="_blank" rel="noreferrer"
                className={`brut-btn brut-btn-primary w-full ${!customPhone ? "opacity-60" : ""}`}
                data-testid="wa-custom-btn"
              >
                <MessageSquare size={16} strokeWidth={3} /> SEND TO CUSTOM NUMBER
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
